import { useEffect, useRef, useState } from 'react';

export interface Coords {
  lat: number;
  lng: number;
  accuracy?: number;
  heading?: number;
}

export type GeoState =
  | { status: 'idle' }
  | { status: 'locating' }
  | { status: 'ok'; coords: Coords }
  | { status: 'denied' }
  | { status: 'unavailable'; message: string };

/**
 * Posição contínua do aparelho.
 *
 * `watchPosition` e não `getCurrentPosition` em laço: o navegador só
 * entrega atualização quando o aparelho de fato se move, o que gasta menos
 * bateria — que é o recurso mais escasso de um motorista em turno.
 */
export function useGeolocation(enabled: boolean): GeoState & { retry: () => void } {
  const [state, setState] = useState<GeoState>({ status: 'idle' });
  // Permite pedir a localização de novo depois de uma negativa: em alguns
  // navegadores a segunda tentativa reabre o pedido de permissão, e em
  // todos ela recupera um erro temporário de sinal.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }
    if (!('geolocation' in navigator)) {
      setState({ status: 'unavailable', message: 'este aparelho não tem GPS disponível' });
      return;
    }

    setState({ status: 'locating' });

    const id = navigator.geolocation.watchPosition(
      (pos) =>
        setState({
          status: 'ok',
          coords: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            heading: pos.coords.heading ?? undefined,
          },
        }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setState({ status: 'denied' });
        else setState({ status: 'unavailable', message: err.message });
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
    );

    return () => navigator.geolocation.clearWatch(id);
  }, [enabled, attempt]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}

/**
 * Mantém a tela acesa enquanto o motorista está online.
 *
 * PWA não tem geolocalização em segundo plano: com a tela apagada ou o app
 * fora de foco, o GPS para e o passageiro deixa de ver o carro se mexendo.
 * O wake lock é o que torna o app do motorista viável na web — junto com o
 * celular no suporte do painel, que é como se trabalha de verdade.
 *
 * O sistema solta o lock sozinho quando a aba perde o foco, então
 * readquirimos ao voltar.
 */
export function useWakeLock(enabled: boolean): { supported: boolean; active: boolean } {
  const sentinel = useRef<WakeLockSentinel | null>(null);
  const [active, setActive] = useState(false);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!enabled || !supported) {
      void sentinel.current?.release().catch(() => undefined);
      sentinel.current = null;
      setActive(false);
      return;
    }

    let cancelled = false;

    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void lock.release().catch(() => undefined);
          return;
        }
        sentinel.current = lock;
        setActive(true);
        lock.addEventListener('release', () => setActive(false));
      } catch {
        setActive(false);
      }
    };

    void acquire();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !sentinel.current) void acquire();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel.current?.release().catch(() => undefined);
      sentinel.current = null;
    };
  }, [enabled, supported]);

  return { supported, active };
}

/** Vibração curta para confirmação tátil. Silenciosa onde não existe. */
export function haptic(pattern: number | number[] = 12): void {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
}
